import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectSelectedItem, selectItemsForParent, selectProjectId } from '@/store/selectors';
import { setSelectedItem } from '../../app/features/project/projectSlice';
import { Message } from './components/Message';
import type { NormalizedItem } from '@/components/itemList/types';

interface TaskMessage {
  content: string;
  created: string;
  sender: string;
  userData?: { given_name?: string; family_name?: string; img?: string };
}

interface Task {
  id: string;
  resource: string;
  type: string;
  taskType: string;
  timestamp?: number;
  owner: string;
  /** Presigned URL of the task's own snapshot frame, when available */
  preview?: string;
  messages: TaskMessage[];
}

interface ProjectChatTasksProps {
  tasks: Task[];
  updateSelectedTask: (task: Task) => void;
  isVisible: boolean;
  projectId: string;
  uid: string | null;
  userRole: string | null;
  updateModalOpen: (type: string, params?: Record<string, unknown>) => void;
}

export function ProjectChatTasks({
  tasks,
  updateSelectedTask,
  isVisible,
  projectId,
  uid,
  userRole,
  updateModalOpen,
}: ProjectChatTasksProps) {
  const dispatch = useAppDispatch();
  const selectedItem = useAppSelector(selectSelectedItem) as NormalizedItem | null;
  const reduxProjectId = useAppSelector(selectProjectId) as string | null;
  const items = useAppSelector(selectItemsForParent(reduxProjectId)) as NormalizedItem[] | undefined;
  // Resolve senders from the live users list at render time, falling back to the
  // snapshot stored at transform time — prevents stale "[Removed user]" entries
  const projectUsers = useAppSelector((state) => state.project.users) as Array<
    { uid: string } & NonNullable<TaskMessage['userData']>
  >;

  return (
    <ul className={isVisible ? '' : 'hidden'}>
      {tasks.map((task) =>
        task.messages.map((message, index) => {
          const isFirst = index === 0;
          const sender = projectUsers.find((u) => u.uid === message.sender) ?? message.userData;
          // Prefer the task's own snapshot frame (annotated frame at the task's
          // timestamp); fall back to the resource thumbnail — for videos that's
          // the upload-time snapshot, absent for formats the browser can't decode
          const imageUrl = isFirst
            ? task.preview || items?.find((i) => i.id === task.resource)?.thumbnailUrl
            : sender?.img;

          return (
            <li key={message.created}>
              <Message
                firstName={sender?.given_name}
                lastName={sender?.family_name}
                created={message.created}
                content={message.content}
                image={{ task: isFirst, imageUrl }}
                isAReply={!isFirst}
                task={task}
                previewItemOnShow={selectedItem ?? undefined}
                taskResource={items?.find((i) => i.id === task.resource)}
                updateSelectedTask={updateSelectedTask}
                isRemovable={isFirst && (userRole === 'admin' || uid === task.owner) && !!task.id}
                projectId={projectId}
                updateModalOpen={updateModalOpen}
              />
            </li>
          );
        }),
      )}
    </ul>
  );
}
